import { toJS } from 'mobx';

import { BernieGraphqlClient, FetchPolicy } from 'bernie-graphql';
import { getErrorString } from 'bernie-logger';
import { Headers } from 'bernie-core/dist/source';
import { Logger, NOOP_LOGGER } from 'bernie-logger';
import { merge, SerializedData } from 'bernie-core';
import { SystemEvent } from 'bernie-logger';

import { SOURCE_EVENTS } from './events/source-events';

import {
  AddPropertyToCart,
  ApiOperations,
  Cart,
  Conversations,
  CreateLodgingConversation,
  CreateQuestion,
  CreateSessionCart,
  Customer,
  EmptyCart,
  FindAnswersSearch,
  FullPropertySearch,
  OperationResult,
  PrepareCheckout,
  PrepareCheckoutByCartId,
  PropertyKeyPhrases,
  PropertyLocationScoreInfo,
  PropertyMapMigration,
  PropertyRoomScoreInfo,
  PropertySearch,
  QuestionAnswers,
  RemoveItemFromCart,
  Reviews,
  TranslateReview,
  UpvotePropertyReview
} from '__generated__/api';

const { withoutKeys } = merge;

// We want to eventually load this from config with the current git hash and environement
const CLIENT_INFO = 'shopping-pwa,unknown,unknown';

const buildBernieGraphQLClientWithHeaders = (host: string, headers: Headers, logger: Logger): BernieGraphqlClient =>
  new BernieGraphqlClient(
    {
      uri: `${host}/graphql`,
      headers: { ...headers, 'Client-Info': CLIENT_INFO }
    },
    logger
  );

export class BexApiSource {
  private readonly api: ApiOperations;
  private readonly logger: Logger;

  constructor(host = '', logger: Logger = NOOP_LOGGER, headers: Headers = {}) {
    this.api = ApiOperations.withClient(buildBernieGraphQLClientWithHeaders(host, headers, logger));
    this.logger = logger;
  }

  public fetchReviews(variables: Reviews.Variables): Promise<OperationResult<Reviews.Query>> {
    return this.api.reviews(variables).catch(this.getErrorHandler(variables, SOURCE_EVENTS.BEX_API_LODGING_REVIEWS_FETCH_ERROR));
  }

  public fetchBexApiPropertySearch(variables: PropertySearch.Variables): Promise<OperationResult<PropertySearch.Query>> {
    return this.api
      .propertySearch(variables, FetchPolicy.NO_CACHE)
      .catch(this.getErrorHandler(variables, SOURCE_EVENTS.BEX_API_LODGING_PROPERTY_SEARCH_FETCH_ERROR));
  }

  public fetchBexApiPropertyMapMigration(variables: PropertySearch.Variables): Promise<OperationResult<PropertyMapMigration.Query>> {
    return this.api
      .propertyMapMigration(variables, FetchPolicy.NO_CACHE)
      .catch(this.getErrorHandler(variables, SOURCE_EVENTS.BEX_API_LODGING_PROPERTY_MAP_MIGRATION_FETCH_ERROR));
  }

  public fetchFullBexApiPropertySearchTemp(variables: PropertySearch.Variables): Promise<OperationResult<FullPropertySearch.Query>> {
    return this.api
      .fullPropertySearch(variables, FetchPolicy.NO_CACHE)
      .catch(this.getErrorHandler(variables, SOURCE_EVENTS.BEX_API_LODGING_FULL_PROPERTY_SEARCH_FETCH_ERROR));
  }

  public fetchTranslatedReview(variables: TranslateReview.Variables): Promise<OperationResult<TranslateReview.Query>> {
    return this.api.translateReview(variables).catch(this.getErrorHandler(variables, SOURCE_EVENTS.BEX_API_UGC_TRANSLATE_REVIEW_ERROR));
  }

  public fetchPropertyRoomScoreInfo(variables: PropertyRoomScoreInfo.Variables): Promise<OperationResult<PropertyRoomScoreInfo.Query>> {
    return this.api
      .propertyRoomScoreInfo(variables)
      .catch(this.getErrorHandler(variables, SOURCE_EVENTS.BEX_API_UGC_ROOM_SCORE_FETCH_ERROR));
  }

  public fetchPropertyLocationScoreInfo(
    variables: PropertyLocationScoreInfo.Variables
  ): Promise<OperationResult<PropertyLocationScoreInfo.Query>> {
    return this.api
      .propertyLocationScoreInfo(variables)
      .catch(this.getErrorHandler(variables, SOURCE_EVENTS.BEX_API_UGC_LOCATION_SCORE_FETCH_ERROR));
  }

  public fetchPropertyKeyPhrases(variables: PropertyKeyPhrases.Variables): Promise<OperationResult<PropertyKeyPhrases.Query>> {
    return this.api
      .propertyKeyPhrases(variables)
      .catch(this.getErrorHandler(variables, SOURCE_EVENTS.BEX_API_UGC_LOCATION_SCORE_FETCH_ERROR));
  }

  public fetchCustomerIntent(variables: Customer.Variables): Promise<OperationResult<Customer.Query>> {
    return this.api.customer(variables).catch(this.getErrorHandler(variables, SOURCE_EVENTS.BEX_API_CUSTOMER_CUSTOMER_INTENT_FETCH_ERROR));
  }

  public createSessionCart(variables: CreateSessionCart.Variables): Promise<OperationResult<CreateSessionCart.Mutation>> {
    return this.api.createSessionCart(variables).catch(this.getErrorHandler(variables, SOURCE_EVENTS.BEX_API_MULTIITEM_CREATE_CART_ERROR));
  }

  public addPropertyToCart(variables: AddPropertyToCart.Variables): Promise<OperationResult<AddPropertyToCart.Mutation>> {
    return this.api
      .addPropertyToCart(variables)
      .catch(this.getErrorHandler(variables, SOURCE_EVENTS.BEX_API_MULTIITEM_ADD_ITEM_TO_CART_ERROR));
  }

  public fetchCart(variables: Cart.Variables): Promise<OperationResult<Cart.Query>> {
    return this.api
      .cart(variables, FetchPolicy.NO_CACHE)
      .catch(this.getErrorHandler(variables, SOURCE_EVENTS.BEX_API_MULTIITEM_CART_FETCH_ERROR));
  }

  public removeItemFromCart(variables: RemoveItemFromCart.Variables): Promise<OperationResult<RemoveItemFromCart.Mutation>> {
    return this.api
      .removeItemFromCart(variables)
      .catch(this.getErrorHandler(variables, SOURCE_EVENTS.BEX_API_MULTIITEM_REMOVE_ITEM_FROM_CART_ERROR));
  }

  public emptyCart(variables: EmptyCart.Variables): Promise<OperationResult<EmptyCart.Mutation>> {
    return this.api
      .emptyCart(variables)
      .catch(this.getErrorHandler(variables, SOURCE_EVENTS.BEX_API_MULTIITEM_REMOVE_ITEM_FROM_CART_ERROR));
  }

  public prepareCheckout(variables: PrepareCheckout.Variables): Promise<OperationResult<PrepareCheckout.Mutation>> {
    return this.api
      .prepareCheckout(variables)
      .catch(this.getErrorHandler(variables, SOURCE_EVENTS.BEX_API_MULTIITEM_PREPARE_CHECKOUT_ERROR));
  }

  public prepareCheckoutByCartId(variables: PrepareCheckoutByCartId.Variables): Promise<OperationResult<PrepareCheckoutByCartId.Mutation>> {
    return this.api
      .prepareCheckoutByCartId(variables)
      .catch(this.getErrorHandler(variables, SOURCE_EVENTS.BEX_API_MULTIITEM_PREPARE_CHECKOUT_BY_CART_ID_ERROR));
  }

  public fetchConversations(variables: Conversations.Variables): Promise<OperationResult<Conversations.Query>> {
    return this.api
      .conversations(variables)
      .catch(this.getErrorHandler(variables, SOURCE_EVENTS.BEX_API_CONVERSATIONS_CONVERSATIONS_FETCH_ERROR));
  }

  public createLodgingConversation(
    variables: CreateLodgingConversation.Variables
  ): Promise<OperationResult<CreateLodgingConversation.Mutation>> {
    return this.api
      .createLodgingConversation(variables)
      .catch(this.getErrorHandler(variables, SOURCE_EVENTS.BEX_API_CONVERSATIONS_CREATE_CONVERSATION_ERROR));
  }

  public upvotePropertyReview(variables: UpvotePropertyReview.Variables): Promise<OperationResult<UpvotePropertyReview.Mutation>> {
    return this.api.upvotePropertyReview(variables).catch(this.getErrorHandler(variables, SOURCE_EVENTS.BEX_API_UGC_UPVOTE_REVIEW_ERROR));
  }

  public fetchFindAnswersSearch(variables: FindAnswersSearch.Variables): Promise<OperationResult<FindAnswersSearch.Query>> {
    return this.api.findAnswersSearch(variables).catch(this.getErrorHandler(variables, SOURCE_EVENTS.BEX_API_UGC_FIND_ANSWERS_FETCH_ERROR));
  }

  public fetchQuestionAnswers(variables: QuestionAnswers.Variables): Promise<OperationResult<QuestionAnswers.Query>> {
    return this.api
      .questionAnswers(variables)
      .catch(this.getErrorHandler(variables, SOURCE_EVENTS.BEX_API_UGC_QUESTION_ANSWERS_FETCH_ERROR));
  }

  public createQuestion(variables: CreateQuestion.Variables): Promise<OperationResult<CreateQuestion.Mutation>> {
    return this.api.createQuestion(variables).catch(this.getErrorHandler(variables, SOURCE_EVENTS.BEX_API_UGC_CREATE_QUESTION_ERROR));
  }

  private getErrorHandler(variables: any, sourceEvent: SystemEvent) {
    return (error: any) => {
      this.logger.logEvent(sourceEvent, variables, getErrorString(error));
      // Throw the error to the next catch block since it is not resolved here
      throw error;
    };
  }

  /**
   * Override the default `toJson` to exclude the Apollo client.
   * If we don't include this, we will get a JSON serialization error.
   *
   * Creates a basic JSON object from the store, excluding
   * any fields provided in `ignoreAdditionalKeys`.
   */
  public toJSON(ignoreAdditionalKeys: string[] | string = []): SerializedData {
    if (!Array.isArray(ignoreAdditionalKeys)) {
      ignoreAdditionalKeys = ignoreAdditionalKeys.constructor === String ? [ignoreAdditionalKeys] : [];
    }

    const regexIgnorePrivate = /^_.+/;
    const simpleObject: any = toJS(this);
    const ignoreKeys: string[] = Object.keys(simpleObject)
      .filter(key => regexIgnorePrivate.test(key) || typeof simpleObject[key] === 'function')
      .concat(ignoreAdditionalKeys, 'logger', 'api', 'headers');

    return withoutKeys(ignoreKeys).merge({}, simpleObject);
  }
}
